/* =========================================================
   Smartly Cooked – Bundle upsell (1 / 2 eBooks, Complete Library or 3 eBooks, + hardcover)
   Custom element <sc-bundle-upsell>, rendered by
   snippets/sc-bundle-upsell.liquid.
   ========================================================= */

if (!customElements.get('sc-bundle-upsell')) {
  customElements.define(
    'sc-bundle-upsell',
    class ScBundleUpsell extends HTMLElement {
      connectedCallback() {
        if (this._initialised) return;
        this._initialised = true;

        const ds = this.dataset;

        this.moneyFormat = ds.moneyFormat || '${{amount}}';
        this.saveLabel = ds.saveLabel || 'Save';
        this.maxTier = Number(ds.maxTier) || 1;

        this.main = {
          id: Number(ds.mainVariant),
          price: Number(ds.mainPrice) || 0,
          list: Number(ds.mainList) || Number(ds.mainPrice) || 0,
        };
        this.mainProduct = String(ds.mainProduct || '');
        this.hardcoverAsTier = ds.hardcoverAsTier === 'true';

        // Safety net: book 1 is the current PDP product and must never be
        // selectable again as book 2 or 3, even if a collection response is
        // cached or contains an unexpected duplicate entry.
        this.querySelectorAll('[data-bu-pick]').forEach((input) => {
          const isCurrentProduct = this.mainProduct && input.dataset.product === this.mainProduct;
          const isCurrentVariant = Number(input.value) === this.main.id;
          if (isCurrentProduct || isCurrentVariant) input.closest('.sc-bu__choice')?.remove();
        });

        this.hardcover = ds.hardcoverVariant
          ? {
              id: Number(ds.hardcoverVariant),
              price: Number(ds.hardcoverPrice) || 0,
              list: Number(ds.hardcoverList) || Number(ds.hardcoverPrice) || 0,
            }
          : null;

        // Complete Library: tier 3 sells one product that holds every eBook.
        this.library = ds.libraryVariant
          ? {
              id: Number(ds.libraryVariant),
              price: Number(ds.libraryPrice) || 0,
              list: Number(ds.libraryList) || Number(ds.libraryPrice) || 0,
            }
          : null;

        // Attributes ending in a digit (data-discount-2) are not camel-cased in dataset, so read them directly.
        this.discount = {
          type: ds.discountType === 'amount' ? 'amount' : ds.discountType === 'bxgy' ? 'bxgy' : 'percent',
          2: Number(this.getAttribute('data-discount-2')) || 0,
          3: Number(this.getAttribute('data-discount-3')) || 0,
        };

        this.fixedPrices = {
          1: Number(this.getAttribute('data-fixed-price-1')) || 0,
          2: Number(this.getAttribute('data-fixed-price-2')) || 0,
          3: Number(this.getAttribute('data-fixed-price-3')) || 0,
        };

        this.codes = {
          1: (this.getAttribute('data-code-1') || '').trim(),
          2: (this.getAttribute('data-code-2') || '').trim(),
          3: (this.getAttribute('data-code-3') || '').trim(),
        };

        // Optional free gift per tier (normally a $0 product; "list" is the value shown to the customer).
        this.gifts = {};
        [1, 2, 3].forEach((tier) => {
          const id = this.getAttribute(`data-gift-${tier}-variant`);
          if (!id) return;
          this.gifts[tier] = {
            id: Number(id),
            price: Number(this.getAttribute(`data-gift-${tier}-price`)) || 0,
            list: Number(this.getAttribute(`data-gift-${tier}-list`)) || 0,
          };
        });
        this.giftBanners = Array.from(this.querySelectorAll('[data-bu-gift]'));

        this.tierCards = Array.from(this.querySelectorAll('[data-bu-tier]'));
        this.tierInputs = Array.from(this.querySelectorAll('[data-bu-tier-input]'));
        this.slots = {
          2: this.querySelector('[data-bu-slot="2"]'),
          3: this.querySelector('[data-bu-slot="3"]'),
        };
        this.hardcoverInput = this.querySelector('[data-bu-hardcover]');
        this.hardcoverOnlyInput = this.querySelector('[data-bu-hardcover-only]');
        this.hardcoverOnlyRow = this.querySelector('[data-bu-hardcover-only-row]');
        this.hardcoverCard = this.querySelector('.sc-bu__hardcover');
        this.submitButton = this.querySelector('[data-bu-submit]');
        this.submitLabel = this.querySelector('.sc-bu__submit-label');
        this.totalLabel = this.querySelector('[data-bu-total]');
        this.errorBox = this.querySelector('[data-bu-error]');
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        const checkedTier = this.tierInputs.find((input) => input.checked);
        this.tier = Math.min(Number(checkedTier ? checkedTier.value : 1) || 1, this.maxTier);

        this.tierInputs.forEach((input) => {
          input.addEventListener('change', () => this.setTier(Number(input.value)));
        });

        this.querySelectorAll('[data-bu-pick]').forEach((input) => {
          input.addEventListener('change', () => this.onPick(input));
        });

        if (this.hardcoverInput) {
          this.hardcoverInput.addEventListener('change', () => this.update());
          if (this.hardcoverInput.disabled && this.hardcoverCard) {
            this.hardcoverCard.classList.add('is-disabled');
          }
        }
        if (this.hardcoverOnlyInput) {
          this.hardcoverOnlyInput.addEventListener('change', () => {
            if (this.hardcoverOnlyInput.checked && this.hardcoverInput) this.hardcoverInput.checked = true;
            this.classList.toggle('is-hardcover-only', this.hardcoverOnlyInput.checked);
            if (this.hardcoverOnlyRow) this.hardcoverOnlyRow.classList.toggle('is-selected', this.hardcoverOnlyInput.checked);
            this.update();
          });
          // "Prefer the hardcover only?" is a sentence: a link selects it, and once
          // selected only the x can undo it.
          const selectButton = this.querySelector('[data-bu-hardcover-only-select]');
          const clearButton = this.querySelector('[data-bu-hardcover-only-clear]');
          if (selectButton) {
            selectButton.addEventListener('click', () => this.setHardcoverOnly(true));
          }
          if (clearButton) {
            clearButton.addEventListener('click', () => this.setHardcoverOnly(false));
          }
          // The dimmed bundle must not react while hardcover-only is selected.
          this.addEventListener('click', (event) => {
            if (!this.hardcoverOnlyInput.checked) return;
            if (event.target.closest('[data-bu-hardcover-only-clear]')) return;
            if (event.target.closest('[data-bu-submit]')) return;
            if (event.target.closest('.sc-bu__tiers, .sc-bu__slots, .sc-bu__gifts, .sc-bu__hardcover')) {
              event.preventDefault();
              event.stopPropagation();
            }
          }, true);
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', () => this.addToCart());
        }

        // Optional dropdown-style pickers.
        this.dropdowns = Array.from(this.querySelectorAll('[data-bu-dropdown]'));
        this.dropdowns.forEach((dd) => {
          const toggle = dd.querySelector('[data-bu-dd-toggle]');
          if (!toggle) return;
          toggle.addEventListener('click', () => this.toggleDropdown(dd));
        });
        if (this.dropdowns.length) {
          document.addEventListener('click', (event) => {
            if (!this.contains(event.target)) this.closeDropdowns();
            else {
              const inside = event.target.closest('[data-bu-dropdown]');
              this.dropdowns.forEach((dd) => {
                if (dd !== inside) this.toggleDropdown(dd, false);
              });
            }
          });
          this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.closeDropdowns();
          });
        }

        this.syncSlots();
        this.setTier(this.tier);
      }

      toggleDropdown(dd, force) {
        const list = dd.querySelector('[data-bu-dd-list]');
        const toggle = dd.querySelector('[data-bu-dd-toggle]');
        if (!list || !toggle) return;
        const open = typeof force === 'boolean' ? force : list.hidden;
        list.hidden = !open;
        dd.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
      }

      closeDropdowns() {
        this.dropdowns.forEach((dd) => this.toggleDropdown(dd, false));
      }

      updateDropdowns() {
        this.dropdowns.forEach((dd) => {
          const slotEl = dd.closest('[data-bu-slot]');
          if (!slotEl) return;
          const picked = this.pick(Number(slotEl.dataset.buSlot));
          if (!picked) return;
          const img = dd.querySelector('[data-bu-dd-img]');
          const title = dd.querySelector('[data-bu-dd-title]');
          const price = dd.querySelector('[data-bu-dd-price]');
          if (img) {
            if (picked.img) {
              if (img.getAttribute('src') !== picked.img) img.setAttribute('src', picked.img);
              img.hidden = false;
            } else {
              img.hidden = true;
            }
          }
          if (title) title.textContent = picked.title;
          if (price) price.textContent = this.formatMoney(picked.price);
        });
      }

      /* ---------- state ---------- */

      setHardcoverOnly(on) {
        if (!this.hardcoverOnlyInput) return;
        this.hardcoverOnlyInput.checked = !!on;
        if (!on && this.hardcoverInput) this.hardcoverInput.checked = false;
        this.hardcoverOnlyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setTier(tier) {
        this.tier = Math.max(1, Math.min(tier, this.maxTier));

        this.tierInputs.forEach((input) => {
          input.checked = Number(input.value) === this.tier;
        });

        this.tierCards.forEach((card) => {
          card.classList.toggle('is-active', Number(card.dataset.buTier) === this.tier);
        });

        // The Complete Library holds every eBook, so no 2nd/3rd book needs picking.
        const libraryTier = !!this.library && this.tier >= 3;
        [2, 3].forEach((slot) => {
          if (this.slots[slot]) this.slots[slot].hidden = libraryTier || this.tier < slot;
        });

        this.giftBanners.forEach((banner) => {
          banner.hidden = Number(banner.dataset.buGift) !== this.tier;
        });

        this.syncSlots();
        this.update();
      }

      slotInputs(slot) {
        return this.slots[slot] ? Array.from(this.slots[slot].querySelectorAll('[data-bu-pick]')) : [];
      }

      pick(slot) {
        const input = this.slotInputs(slot).find((i) => i.checked);
        if (!input) return null;
        return {
          id: Number(input.value),
          product: input.dataset.product,
          price: Number(input.dataset.price) || 0,
          list: Number(input.dataset.list) || Number(input.dataset.price) || 0,
          img: input.dataset.img || '',
          title: input.dataset.title || '',
        };
      }

      onPick(input) {
        const slot = Number(input.closest('[data-bu-slot]').dataset.buSlot);
        this.syncSlots(slot);
        this.update();
        // In dropdown mode a pick closes the list again.
        const dd = input.closest('[data-bu-dropdown]');
        if (dd) this.toggleDropdown(dd, false);
      }

      /*
        Choice 2 is independent and must always remain fully selectable.
        Only when the 3-book tier is active does choice 3 exclude the book
        selected as choice 2.
      */
      syncSlots() {
        const inputs2 = this.slotInputs(2);
        const inputs3 = this.slotInputs(3);
        if (!inputs2.length) return;

        let pick2 = inputs2.find((i) => i.checked);
        if (!pick2) {
          pick2 = inputs2[0];
          pick2.checked = true;
        }

        // A hidden third-book choice must never disable anything in choice 2.
        inputs2.forEach((input) => {
          input.disabled = false;
          input.closest('.sc-bu__choice').classList.remove('is-disabled');
        });

        if (inputs3.length && this.tier >= 3) {
          let pick3 = inputs3.find((i) => i.checked);
          const conflict = pick3 && pick3.dataset.product === pick2.dataset.product;

          if (!pick3 || conflict) {
            const alternative = inputs3.find((i) => i.dataset.product !== pick2.dataset.product);
            if (alternative) {
              if (pick3) pick3.checked = false;
              alternative.checked = true;
              pick3 = alternative;
            }
          }

          const product2 = pick2.dataset.product;

          inputs3.forEach((i) => {
            const disabled = i.dataset.product === product2;
            i.disabled = disabled;
            i.closest('.sc-bu__choice').classList.toggle('is-disabled', disabled);
          });
        } else {
          // Tier 1/2: the third selector is hidden and has no influence.
          inputs3.forEach((input) => {
            input.disabled = false;
            input.closest('.sc-bu__choice').classList.remove('is-disabled');
          });
        }

        // Active state on the choice cards.
        [2, 3].forEach((slot) => {
          this.slotInputs(slot).forEach((i) => {
            i.closest('.sc-bu__choice').classList.toggle('is-active', i.checked);
          });
        });
      }

      /* ---------- pricing ---------- */

      itemsForTier(tier) {
        if (this.hardcoverAsTier && tier === 2 && this.hardcover) return [this.hardcover];
        if (tier >= 3 && this.library) return [this.library];
        const items = [this.main];
        if (tier >= 2) {
          const p2 = this.pick(2);
          if (p2) items.push(p2);
        }
        if (tier >= 3) {
          const p3 = this.pick(3);
          if (p3) items.push(p3);
        }
        return items;
      }

      calc(tier) {
        const items = this.itemsForTier(tier);
        const total = items.reduce((sum, item) => sum + item.price, 0);
        const list = items.reduce((sum, item) => sum + item.list, 0);

        let discount = 0;
        const isLibrary = tier >= 3 && !!this.library;
        if (!isLibrary && tier >= 2 && items.length >= 2) {
          const value = this.discount[Math.min(tier, 3)] || 0;
          // Percentages are rounded per item, the same way Shopify allocates a percentage discount per line.
          if (this.discount.type === 'amount') {
            discount = value;
          } else if (this.discount.type === 'bxgy') {
            // Buy 1 get 1 at X% off: Shopify takes the cheapest eligible book and
            // rounds to the cent; mirror that exactly so page and checkout agree.
            const cheapest = Math.min(...items.map((item) => item.price));
            // Shopify rounds a percentage discount DOWN to the cent (observed: 44.00 × 33.31% = 14.65).
            discount = Math.floor((cheapest * (this.discount[2] || 0)) / 100 + 1e-9);
          } else {
            discount = items.reduce((sum, item) => sum + Math.round((item.price * value) / 100), 0);
          }
        }
        discount = Math.max(0, Math.min(discount, total));

        const isStandaloneHardcover = this.hardcoverAsTier && tier === 2;
        const gift = this.gifts[tier] || { price: 0, list: 0 };
        const configuredFixedPrice = isStandaloneHardcover ? 0 : (this.fixedPrices[Math.min(tier, 3)] || 0);
        const price = configuredFixedPrice > 0 ? configuredFixedPrice + gift.price : total - discount + gift.price;
        const listWithGift = list + gift.list;
        return { items, total, list: listWithGift, discount, price, save: Math.max(0, listWithGift - price) };
      }

      update() {
        for (let tier = 1; tier <= this.maxTier; tier++) {
          const result = this.calc(tier);

          const priceEl = this.querySelector(`[data-bu-price="${tier}"]`);
          const compareEl = this.querySelector(`[data-bu-compare="${tier}"]`);
          const saveEl = this.querySelector(`[data-bu-save="${tier}"]`);
          const saveAmountEl = this.querySelector(`[data-bu-save-amount="${tier}"]`);

          if (priceEl) priceEl.textContent = this.formatMoney(result.price);
          if (compareEl) {
            compareEl.textContent = this.formatMoney(result.list);
            compareEl.hidden = result.save <= 0;
          }
          if (saveEl) saveEl.hidden = result.save <= 0;
          if (saveAmountEl) saveAmountEl.textContent = this.formatMoney(result.save);

          // Book covers in the stack follow the customer's picks.
          const card = this.tierCards.find((c) => Number(c.dataset.buTier) === tier);
          if (card) {
            [2, 3].forEach((slot) => {
              const img = card.querySelector(`[data-bu-stack-img="${slot}"]`);
              if (!img) return;
              const picked = this.pick(slot);
              if (picked && picked.img) {
                if (img.getAttribute('src') !== picked.img) img.setAttribute('src', picked.img);
                img.hidden = false;
              } else {
                img.hidden = true;
              }
            });
          }
        }

        this.updateDropdowns();

        const hardcoverOnly = this.hardcoverOnlyInput && this.hardcoverOnlyInput.checked;
        const hardcoverOn = this.hardcoverInput && this.hardcoverInput.checked && !this.hardcoverInput.disabled;
        if (this.hardcoverCard) this.hardcoverCard.classList.toggle('is-active', !!hardcoverOn);

        const total = hardcoverOnly && this.hardcover ? this.hardcover.price : this.calc(this.tier).price + (hardcoverOn ? this.hardcover.price : 0);
        if (this.totalLabel) this.totalLabel.textContent = this.formatMoney(total);

        // Keep the red countdown percentage identical to the price and
        // compare-at price currently shown on the selected PDP choice.
        const activeResult = hardcoverOnly && this.hardcover
          ? { price: this.hardcover.price, list: this.hardcover.list }
          : this.calc(this.tier);
        const countdownDiscount = this.closest('.sc-hero__info')?.querySelector('[data-sc-discount-percentage]');
        if (countdownDiscount && activeResult.list > activeResult.price) {
          const percentage = Math.round(((activeResult.list - activeResult.price) / activeResult.list) * 100);
          const suffix = countdownDiscount.textContent.replace(/^\s*\d+%\s*/, '').trim() || 'discount';
          countdownDiscount.textContent = `${percentage}% ${suffix}`;
          countdownDiscount.dataset.scDiscountSynced = 'true';
        }
        if (!this._countdownResync) {
          // The hero countdown script can initialise after us; re-apply once the page has fully loaded.
          this._countdownResync = true;
          window.addEventListener('load', () => this.update(), { once: true });
          document.addEventListener('shopify:section:load', () => this.update());
        }

        if (this.submitLabel) {
          this.submitLabel.textContent = this.tier === 2
            ? 'ADD HARDCOVER TO CART'
            : (this.tier >= 3 ? 'ADD COMPLETE LIBRARY TO CART' : 'ADD DIGITAL TO CART');
        }
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const placeholder = match ? match[1] : 'amount';
        const value = (Number(cents) || 0) / 100;

        const withDelimiters = (number, precision, thousands, decimal) => {
          const fixed = number.toFixed(precision);
          const parts = fixed.split('.');
          parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          return parts.length > 1 && precision > 0 ? parts.join(decimal) : parts[0];
        };

        let formatted;
        switch (placeholder) {
          case 'amount_no_decimals':
            formatted = withDelimiters(value, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            formatted = withDelimiters(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            formatted = withDelimiters(value, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            formatted = withDelimiters(value, 2, "'", '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            formatted = withDelimiters(value, 0, ' ', '.');
            break;
          case 'amount_with_space_separator':
            formatted = withDelimiters(value, 2, ' ', ',');
            break;
          case 'amount_with_period_and_space_separator':
            formatted = withDelimiters(value, 2, ' ', '.');
            break;
          default:
            formatted = withDelimiters(value, 2, ',', '.');
        }

        return format.replace(/\{\{\s*\w+\s*\}\}/, formatted).trim();
      }

      /* ---------- cart ---------- */

      cartItems() {
        if (this.hardcoverAsTier && this.tier === 2 && this.hardcover) {
          const items = [];
          const gift = this.gifts[2];
          if (gift) items.push({ id: gift.id, quantity: 1 });
          items.push({
            id: this.hardcover.id,
            quantity: 1,
            properties: { 'Includes': 'Digital eBook' },
          });
          return items;
        }
        const hardcoverOnly = this.hardcoverOnlyInput && this.hardcoverOnlyInput.checked && this.hardcover;
        if (hardcoverOnly) return [{ id: this.hardcover.id, quantity: 1 }];
        // Shopify lists the most recently added line first, so add the
        // extras first and the main product last.
        const items = [];
        const gift = this.gifts[this.tier];
        if (gift) items.push({ id: gift.id, quantity: 1 });

        const hardcoverOn = this.hardcoverInput && this.hardcoverInput.checked && !this.hardcoverInput.disabled;
        if (hardcoverOn) items.push({ id: this.hardcover.id, quantity: 1 });

        if (this.tier >= 3 && this.library) {
          items.push({ id: this.library.id, quantity: 1 });
          return items;
        }

        const picks = this.itemsForTier(this.tier).slice(1).reverse();
        picks.forEach((pick) => items.push({ id: pick.id, quantity: 1 }));

        items.push({ id: this.main.id, quantity: 1 });
        return items;
      }

      showError(message) {
        if (!this.errorBox) return;
        this.errorBox.textContent = message || '';
        this.errorBox.hidden = !message;
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      addToCart() {
        if (!this.submitButton || this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.showError();
        this.setLoading(true);

        const body = { items: this.cartItems() };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        const hardcoverOnly = (this.hardcoverAsTier && this.tier === 2) || (this.hardcoverOnlyInput && this.hardcoverOnlyInput.checked);
        const code = hardcoverOnly ? '' : this.codes[Math.min(this.tier, 3)];

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status || !code) return response;

            // Apply the bundle discount code to the cart so the drawer shows the discounted total.
            const updateConfig = fetchConfig('javascript');
            updateConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            const updateBody = { discount: code };
            if (this.cart) {
              updateBody.sections = body.sections;
              updateBody.sections_url = body.sections_url;
            }
            updateConfig.body = JSON.stringify(updateBody);

            return fetch(`${routes.cart_update_url}`, updateConfig)
              .then((r) => r.json())
              .then((updated) => {
                // Fallback for stores where update.js ignores the code: set the checkout discount cookie.
                fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
                if (updated && updated.sections) response.sections = updated.sections;
                return response;
              })
              .catch(() => response);
          })
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || (window.cartStrings && window.cartStrings.error));
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const items = response.items || [response];
            const wantedId = this.hardcoverAsTier && this.tier === 2 && this.hardcover
              ? this.hardcover.id
              : (this.tier >= 3 && this.library ? this.library.id : this.main.id);
            const mainLine = items.find((item) => item.variant_id === wantedId) || items[0];
            if (mainLine) response.key = mainLine.key;

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: wantedId,
                cartData: response,
              });
            }

            this.cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.showError((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.setLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          });
      }
    }
  );
}
